import React, { useState, useEffect } from 'react';

const PartForm = ({ part, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    category: '',
    supplier: '',
    cost_price: '',
    selling_price: '',
    stock_quantity: '',
    min_stock_alert: '',
    location: '',
    barcode: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (part) {
      setFormData({
        name: part.name || '',
        reference: part.reference || '',
        category: part.category || '',
        supplier: part.supplier || '',
        cost_price: part.cost_price || '',
        selling_price: part.selling_price || '',
        stock_quantity: part.stock_quantity || '',
        min_stock_alert: part.min_stock_alert || '',
        location: part.location || '',
        barcode: part.barcode || ''
      });
    }
  }, [part]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (formData.cost_price && isNaN(parseFloat(formData.cost_price))) {
      newErrors.cost_price = 'Le prix d\'achat doit être un nombre';
    }

    if (formData.selling_price && isNaN(parseFloat(formData.selling_price))) {
      newErrors.selling_price = 'Le prix de vente doit être un nombre';
    }

    if (formData.stock_quantity && isNaN(parseInt(formData.stock_quantity))) {
      newErrors.stock_quantity = 'La quantité doit être un nombre entier';
    }

    if (formData.min_stock_alert && isNaN(parseInt(formData.min_stock_alert))) {
      newErrors.min_stock_alert = 'Le seuil d\'alerte doit être un nombre entier';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Convertir les nombres
      const dataToSave = {
        ...formData,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        selling_price: formData.selling_price ? parseFloat(formData.selling_price) : null,
        stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : 0,
        min_stock_alert: formData.min_stock_alert ? parseInt(formData.min_stock_alert) : 0
      };

      await onSave(dataToSave);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="part-form-overlay">
      <div className="part-form-container">
        <div className="part-form-header">
          <h2>{part ? 'Modifier la pièce' : 'Nouvelle pièce'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="part-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nom de la pièce *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="Ex: Filtre à huile"
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="reference">Référence</label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                placeholder="Ex: FO-001"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Catégorie</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Sélectionner une catégorie</option>
                <option value="Filtres">Filtres</option>
                <option value="Huiles">Huiles</option>
                <option value="Freinage">Freinage</option>
                <option value="Moteur">Moteur</option>
                <option value="Transmission">Transmission</option>
                <option value="Électrique">Électrique</option>
                <option value="Carrosserie">Carrosserie</option>
                <option value="Pneumatiques">Pneumatiques</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="supplier">Fournisseur</label>
              <input
                type="text"
                id="supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                placeholder="Ex: Bosch, Valeo..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cost_price">Prix d'achat (€)</label>
              <input
                type="number"
                step="0.01"
                id="cost_price"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                className={errors.cost_price ? 'error' : ''}
                placeholder="0.00"
              />
              {errors.cost_price && <span className="error-text">{errors.cost_price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="selling_price">Prix de vente (€)</label>
              <input
                type="number"
                step="0.01"
                id="selling_price"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleChange}
                className={errors.selling_price ? 'error' : ''}
                placeholder="0.00"
              />
              {errors.selling_price && <span className="error-text">{errors.selling_price}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stock_quantity">Quantité en stock</label>
              <input
                type="number"
                id="stock_quantity"
                name="stock_quantity"
                value={formData.stock_quantity}
                onChange={handleChange}
                className={errors.stock_quantity ? 'error' : ''}
                placeholder="0"
              />
              {errors.stock_quantity && <span className="error-text">{errors.stock_quantity}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="min_stock_alert">Seuil d'alerte</label>
              <input
                type="number"
                id="min_stock_alert"
                name="min_stock_alert"
                value={formData.min_stock_alert}
                onChange={handleChange}
                className={errors.min_stock_alert ? 'error' : ''}
                placeholder="5"
              />
              {errors.min_stock_alert && <span className="error-text">{errors.min_stock_alert}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">Emplacement</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Étagère A-1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="barcode">Code-barres</label>
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                placeholder="Code-barres de la pièce"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Sauvegarde...' : (part ? 'Mettre à jour la pièce' : 'Créer la pièce')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .part-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .part-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .part-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
          }

          .part-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #111827;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .part-form {
            padding: 0 24px 24px 24px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .form-group label {
            font-weight: 500;
            color: #374151;
            font-size: 0.875rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group select.error,
          .form-group textarea.error {
            border-color: #ef4444;
          }

          .error-text {
            color: #ef4444;
            font-size: 0.75rem;
            margin-top: 4px;
          }

          .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e5e7eb;
          }

          .btn-cancel,
          .btn-submit {
            padding: 12px 24px;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.875rem;
          }

          .btn-cancel {
            background: #f3f4f6;
            color: #374151;
          }

          .btn-cancel:hover {
            background: #e5e7eb;
          }

          .btn-submit {
            background: #f59e0b;
            color: white;
          }

          .btn-submit:hover:not(:disabled) {
            background: #d97706;
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @media (max-width: 768px) {
            .part-form-overlay {
              padding: 10px;
            }

            .part-form-container {
              max-width: 100%;
            }

            .form-row {
              grid-template-columns: 1fr;
              gap: 16px;
            }

            .part-form-header {
              padding: 16px 16px 0 16px;
            }

            .part-form {
              padding: 0 16px 16px 16px;
            }

            .form-actions {
              flex-direction: column;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PartForm;
